import React from 'react';
import NavigationPanel from '../NavigationPanel/NavigationPanel';
import { store } from '../../../stores/store';
import { useFetchReportList } from './ReportsAPIHook/useFetchReportList';
import { IFavoriteReport, IMemorizedReport, MemorizedReportsRequest } from './ReportsAPIHook/reportTypes';
import { toast } from 'react-toastify';
import { RedisCacheObject } from '../../../provider/RedisProvider';
import { toJS } from 'mobx';
import { IDepartment, IHospital } from '../FiltersDisplay/IFilterHospitalDept';

interface NavigationPanelMenuProps {
    reportId: number | undefined;
    holderId: number | undefined;
    userId: number | undefined;
    reportTitle: string;
}

function normalizeId(id: string | number | null | undefined): number | null {
    if (id === null || id === undefined) return null;
    const n = typeof id === 'string' ? Number(id) : id;
    return Number.isInteger(n) ? n : null;
}

function uniqueIds(arr: number[]) {
    const seen: { [key: number]: true } = {};
    const out: number[] = [];
    for (let i = 0; i < arr.length; i++) {
        const v = arr[i];
        if (!seen[v]) {
            seen[v] = true;
            out.push(v);
        }
    }
    return out;
}

const NavigationPanelMenu: React.FC<NavigationPanelMenuProps> = ({
    reportId,
    holderId,
    userId,
    reportTitle,
}) => {

    const { commonStore } = store;

    const redisCache = toJS(commonStore.redisCache) as RedisCacheObject;

    const [hospitals] = React.useState<IHospital[]>(redisCache?.Value?.Authorized?.Hospitals || []);
    const [departments] = React.useState<IDepartment[]>(redisCache?.Value?.Authorized?.HospitalDepartments || []);

    const hospitalIds = uniqueIds(hospitals.map(h => normalizeId(h.HospitalId)).filter(id => id !== null) as number[]);
    const departmentIds = uniqueIds(departments.map(d => normalizeId(d.DepartmentId)).filter(id => id !== null) as number[]);

    // Fetch favorite reports
    const {
        reportList: favoriteReportList,
        loading: isFavoriteLoading,
        error: favoriteError,
    } = useFetchReportList<IFavoriteReport>({
        apiUrl: userId
            ? `${process.env.REACT_APP_REPORT_API_URL}favoritereports?userId=${userId}`
            : undefined,
        reportId,
        type: 'favorite',
        body: undefined
    });

    // Build the request body
    const requestBody: MemorizedReportsRequest = {
        userId,
        reportId,
        holderId,
        hospitalIds,
        departmentIds
    };

    // Fetch memorized reports
    const {
        reportList: memorizedReportList,
        loading: isMemorizedLoading,
        error: memorizedError,
    } = useFetchReportList<IMemorizedReport>({
        apiUrl: userId && reportId && holderId
            ? `${process.env.REACT_APP_REPORT_API_URL}memorizedreports`
            : undefined,
        reportId,
        type: 'memorized',
        body: requestBody
    });

    // Helper to construct custom URLs
    const getCustomURL = (customURL: string): string => {
        if (customURL && customURL.trim() !== '') {
            return customURL.includes('.asp')
                ? `${process.env.REACT_APP_QSIGHT_URL}${customURL.replace(window.location.origin, '')}`
                : `${customURL}?key=${store.commonStore.redisKey}`;
        }
        return `${process.env.REACT_APP_QSIGHT_URL}report_view.asp`;
    };

    // Map favorite reports
    const mappedFavoriteReports = (favoriteReportList ?? [])
        .filter((fav: IFavoriteReport) => fav.reportID !== reportId)
        .map((fav: IFavoriteReport) => ({
            id: fav.reportID,
            name: fav.reportName,
            url: getCustomURL(fav.customURL),
        }));

    // Map memorized reports
    const mappedCustomReports = (memorizedReportList ?? []).map((report: IMemorizedReport) => ({
        id: report.memorizedReportId,
        name: report.savedName,
        url: `${report.customURL}?key=${store.commonStore.redisKey}&memid=${report.memorizedReportId}`
    }));

    // Handle loading state
    if (isFavoriteLoading || isMemorizedLoading) {
        return <p>Loading...</p>;
    }

    // Handle error state
    if (favoriteError || memorizedError) {
        toast.error('Oops! Something went wrong while fetching the report list. Please try again later or contact support if the issue persists.');
    }

    return (
        <NavigationPanel
            customReports={mappedCustomReports}
            favoriteReports={mappedFavoriteReports}
            customTitle={reportTitle}
        />
    );
};

export default NavigationPanelMenu;
